import { useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Code,
  CopyButton,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconCopy, IconKey, IconAlertTriangle } from '@tabler/icons-react';
import type { User } from '../../types/api';

interface Props {
  user: User;
  tempPassword: string;
  onDone: () => void;
  heading?: string;
}

export function NewUserCredentialsPanel({ user, tempPassword, onDone, heading }: Props) {
  const [revealed, setRevealed] = useState(false);

  return (
    <Stack gap="md">
      <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={18} />}>
        This is the only time this password will be shown. Copy it now and share it with the user
        through a secure channel. The app will not be able to retrieve it again.
      </Alert>

      <Paper withBorder p="md" radius="md">
        <Stack gap="xs">
          <Text size="xs" c="dimmed" fw={500} tt="uppercase">
            {heading ?? 'New account'}
          </Text>
          <Group gap="xs">
            <Text size="sm" fw={600}>
              Username:
            </Text>
            <Code>{user.username}</Code>
          </Group>
          <Group gap="xs">
            <Text size="sm" fw={600}>
              Email:
            </Text>
            <Code>{user.email}</Code>
          </Group>
          <Group gap="xs" align="center">
            <Text size="sm" fw={600}>
              Temp password:
            </Text>
            {revealed ? (
              <>
                <Code style={{ fontSize: 14 }}>{tempPassword}</Code>
                <CopyButton value={tempPassword} timeout={2000}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Copied' : 'Copy'}>
                      <ActionIcon variant="subtle" onClick={copy} aria-label="copy password">
                        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </>
            ) : (
              <Button
                size="xs"
                variant="light"
                leftSection={<IconKey size={14} />}
                onClick={() => setRevealed(true)}
              >
                Reveal
              </Button>
            )}
          </Group>
        </Stack>
      </Paper>

      <Text size="xs" c="dimmed">
        The user must change this password on their first login.
      </Text>

      <Group justify="flex-end">
        <Button onClick={onDone}>I have saved the password</Button>
      </Group>
    </Stack>
  );
}
